import { useQueryClient } from "@tanstack/vue-query";
import { Ref } from "vue";

import { useV4bMutation, V4BMutationOpts } from "@/composables/tanstack/use-generic-mutation";
import { useServices } from "@/configuration/plugins/app/app-provider.configuration";
import { SiteQueryKeys } from "@/data/api/site-query-keys";
import { SiteProgrammingRangeQueryKeys } from "@/data/api/v4b/site-programming-range/site-programming-range-query-keys";
import { SiteSectionMutationKeys } from "@/data/api/v4b/site-section/site-section-mutation-keys";
import { V4HSiteId } from "@/data/common/ids";
import { CreateSiteSectionRequest } from "@/domains/v4b/site-section/site-section.types";

export function useCreateSectionMutation(siteId: Ref<V4HSiteId | undefined>, opts?: V4BMutationOpts) {
  const v4b = useServices().v4b.clientSdk;
  const queryClient = useQueryClient();

  return useV4bMutation({
    mutationKey: siteId.value ? SiteSectionMutationKeys.create(siteId.value) : undefined,
    mutationFn: (command: CreateSiteSectionRequest) => {
      if (!siteId.value) {
        throw new Error("Site ID is required");
      }

      return v4b.siteSection.create(siteId.value, command);
    },
    onSettled: async () => {
      if (!siteId.value) {
        throw new Error("Site ID is required");
      }

      await queryClient.invalidateQueries({
        queryKey: SiteQueryKeys.sections(siteId.value),
      });

      await queryClient.removeQueries({
        queryKey: SiteProgrammingRangeQueryKeys.findPlanning(siteId.value),
      });
    },
    retry: 0,
    ...opts,
  });
}
